import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { consola } from 'consola'
import { useCartStore } from '@entities/order'

// Logger от Nuxt (consola)
const logger = consola.withTag('basket-state')

/**
 * ✅ ЕДИНЫЙ ИСТОЧНИК ИСТИНЫ для состояния корзины
 * 
 * Этот store хранит ВСЁ состояние связанное с корзиной:
 * - Промокоды
 * - Баллы лояльности
 * - Доставка
 * - Расчеты
 * 
 * Больше НЕТ дублирования данных между useBasketLogic, usePromocode и checkoutForm!
 */
export const useBasketState = defineStore('basketState', () => {
  // ========================================
  // ПРОМОКОД
  // ========================================
  
  /**
   * Код промокода (введенный пользователем)
   */
  const promocode = ref('')
  
  // 🔍 ДИАГНОСТИКА: Отслеживаем изменения промокода
  watch(promocode, (newValue, oldValue) => {
    logger.info('🔔 [basketState] Промокод изменился:', {
      oldValue,
      newValue,
      timestamp: new Date().toISOString(),
    })
  })
  
  /**
   * Скидка от промокода (в рублях)
   */
  const promocodeDiscount = ref(0)

  /**
   * Скидка «подарок за сумму» (в рублях) — цена одной бесплатной единицы подарочного
   * блюда, которое лежит в корзине по полной цене.
   *
   * ⚠️ Обязана участвовать во всех суммах: без неё гость платит за «бесплатный»
   * подарок и сервисный сбор с него (касса при этом списывает меньше — цифры расходятся).
   * Источник — useGiftTiers, зеркало `giftTierDiscount` из ответа /orders/calculate.
   */
  const giftTierDiscount = ref(0)

  /**
   * Скидка «подарок за частоту заказов» (в рублях) — своя механика со своим подарком.
   *
   * Отдельное поле, а не общее с подарком за сумму: обе механики пишут независимо, и
   * общий ref они бы затирали друг другу. В суммах участвуют вместе (в заказе подарок
   * один, поэтому одновременно ненулевой обычно только один из них).
   *
   * ⚠️ Без этой величины в фолбэк-расчёте (когда preview с бэка недоступен — например,
   * доставка без указанного адреса) строка показывала «Бесплатно», а итог включал полную
   * цену подарка.
   */
  const weeklyGiftDiscount = ref(0)

  /**
   * Применен ли промокод
   */
  const isPromocodeApplied = ref(false)
  
  /**
   * Дает ли промокод бесплатную доставку
   */
  const isFreeDelivery = ref(false)
  
  /**
   * Совместим ли промокод с программой лояльности
   */
  const isPromocodeCompatibleWithLoyalty = ref(true)
  
  /**
   * ID примененного промокода (для отправки на сервер)
   */
  const appliedPromocodeId = ref<string | null>(null)
  
  /**
   * Ошибка валидации промокода
   */
  const promocodeError = ref<string | null>(null)
  
  /**
   * Идет ли загрузка промокода
   */
  const promocodeLoading = ref(false)

  /**
   * Запрошено авто-применение промокода (промокод пришёл из пуша/диплинка).
   * BasketMobile отслеживает флаг, сам вызывает валидацию (и авто-добавление подарка)
   * и сбрасывает флаг. Отличает промокод «из пуша» от ручного ввода в UI.
   */
  const autoApplyRequested = ref(false)

  // ========================================
  // БАЛЛЫ ЛОЯЛЬНОСТИ
  // ========================================
  
  /**
   * Использовать ли баллы лояльности для оплаты
   */
  const useLoyaltyPoints = ref(false)
  
  /**
   * Количество баллов к списанию
   */
  const pointsToUse = ref(0)
  
  /**
   * Доступные баллы пользователя
   */
  const availablePoints = ref(0)
  
  /**
   * Максимальное количество баллов которое можно списать
   */
  const maxPointsToUse = ref(0)
  
  /**
   * Кешбэк который начислится за заказ (в рублях)
   */
  const cashbackAmount = ref(0)
  
  /**
   * Процент кешбэка
   */
  const cashbackLevel = ref('3%')
  
  // ========================================
  // ДОСТАВКА
  // ========================================
  
  /**
   * ID выбранной зоны доставки
   */
  const deliveryZoneId = ref<string | null>(null)
  
  /**
   * Стоимость доставки (вычисляется на основе зоны и суммы заказа)
   */
  const deliveryCost = ref(0)
  
  /**
   * Минимальная сумма заказа для доставки
   */
  const minOrderAmount = ref(0)
  
  // ========================================
  // РАСЧЕТЫ
  // ========================================
  
  /**
   * Процент сервисного сбора
   */
  const serviceFeePercent = ref(0)
  
  /**
   * Сумма сервисного сбора (в рублях)
   */
  const serviceFee = ref(0)
  
  /**
   * Финальная сумма к оплате (с учетом всех скидок, доставки и сборов)
   */
  const finalAmount = ref(0)
  
  // ========================================
  // COMPUTED (читаем из других stores)
  // ========================================
  
  // ✅ Создаем ссылку на cart store ОДИН РАЗ при инициализации
  // Это критично для сохранения реактивности между stores
  const cartStore = useCartStore()
  
  logger.info('🚀 BasketState initialized', {
    cartStoreExists: !!cartStore,
    initialCartCount: cartStore.cart?.length ?? 0,
    hasCart: !!cartStore.cart,
  })
  
  /**
   * Товары в корзине (из cart store)
   */
  const cartItems = computed(() => {
    // ⚠️ В Pinia computed refs автоматически unwrap'ятся, поэтому НЕ нужно .value
    const items = cartStore.cart
    logger.debug('🛒 BasketState.cartItems computed', { 
      itemsCount: items.length,
      items: items.length > 0 ? items.map((i: any) => ({ id: i.id, name: i.name })) : 'empty',
      cartStoreExists: !!cartStore,
      cartExists: !!cartStore.cart,
    })
    return items
  })
  
  // ✅ Добавляем watch для отладки изменений корзины
  watch(
    () => cartStore.cart, // В Pinia computed refs автоматически unwrap'ятся
    (newCart, oldCart) => {
      logger.info('🔄 Cart changed in basketState', {
        oldCount: oldCart?.length ?? 0,
        /* v8 ignore next */
        newCount: newCart?.length ?? 0,
        hasItems: newCart && newCart.length > 0,
      })
    },
    { deep: true },
  )
  
  /**
   * Общая стоимость товаров в корзине (без скидок)
   */
  const cartTotal = computed(() => {
    const total = cartStore.totalPrice // В Pinia computed refs автоматически unwrap'ятся
    logger.debug('💰 BasketState.cartTotal computed', { total })
    return total
  })
  
  /**
   * Количество товаров в корзине
   */
  const cartItemsCount = computed(() => {
    const count = cartStore.totalItems // В Pinia computed refs автоматически unwrap'ятся
    logger.debug('🔢 BasketState.cartItemsCount computed', { count })
    return count
  })
  
  // ========================================
  // ВЫЧИСЛЕНИЯ (для удобства)
  // ========================================
  
  /**
   * Сумма после применения промокода
   */
  const amountAfterPromocode = computed(() => {
    return Math.max(0, cartTotal.value - promocodeDiscount.value)
  })
  
  /**
   * Сумма после списания баллов
   */
  const amountAfterPoints = computed(() => {
    const discount = useLoyaltyPoints.value ? pointsToUse.value : 0
    return Math.max(0, amountAfterPromocode.value - discount)
  })

  /**
   * Сумма после ВСЕХ скидок, включая подарок за сумму.
   * Именно она — база сервисного сбора и итога (зеркало `baseAmountForServiceFee`
   * из `order-pricing.ts` на бэкенде, но без доставки).
   */
  const amountAfterDiscounts = computed(() => {
    return Math.max(0, amountAfterPoints.value - giftTierDiscount.value - weeklyGiftDiscount.value)
  })

  /**
   * Итоговая сумма с доставкой и сервисным сбором
   */
  const totalWithDeliveryAndFee = computed(() => {
    return amountAfterDiscounts.value + deliveryCost.value + serviceFee.value
  })
  
  // ========================================
  // МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ СОСТОЯНИЕМ
  // ========================================
  
  /**
   * Применить промокод
   */
  function applyPromocode(result: {
    discount: number
    freeDelivery: boolean
    compatibleWithLoyalty: boolean
    promocodeId: string
  }) {
    promocode.value = promocode.value.toUpperCase()
    promocodeDiscount.value = result.discount
    isFreeDelivery.value = result.freeDelivery
    isPromocodeCompatibleWithLoyalty.value = result.compatibleWithLoyalty
    appliedPromocodeId.value = result.promocodeId
    isPromocodeApplied.value = true
    promocodeError.value = null

    // Промокод несовместим с баллами: блок списания в корзине прячется, но выбранные
    // ранее баллы оставались в состоянии и продолжали уходить в расчёт. Гость видел
    // сумму со скидкой И списанием, а на оформлении получал отказ
    // PROMOCODE_INCOMPATIBLE_WITH_LOYALTY. Снимаем списание сразу.
    if (!result.compatibleWithLoyalty && (useLoyaltyPoints.value || pointsToUse.value > 0)) {
      useLoyaltyPoints.value = false
      pointsToUse.value = 0
      logger.info('BasketState', 'Промокод несовместим с баллами — списание снято')
    }

    logger.info('BasketState', 'Promocode applied', {
      code: promocode.value,
      discount: result.discount,
      freeDelivery: result.freeDelivery,
      compatibleWithLoyalty: result.compatibleWithLoyalty,
    })
  }
  
  /**
   * Сбросить промокод
   */
  function resetPromocode() {
    promocode.value = ''
    promocodeDiscount.value = 0
    isPromocodeApplied.value = false
    isFreeDelivery.value = false
    isPromocodeCompatibleWithLoyalty.value = true
    appliedPromocodeId.value = null
    promocodeError.value = null
    
    logger.debug('BasketState', 'Promocode reset')
  }
  
  /**
   * Установить ошибку промокода
   */
  function setPromocodeError(error: string | null) {
    promocodeError.value = error
  }
  
  /**
   * Установить состояние загрузки промокода
   */
  function setPromocodeLoading(loading: boolean) {
    promocodeLoading.value = loading
  }
  
  /**
   * Установить количество баллов к списанию
   */
  function setPointsToUse(points: number) {
    pointsToUse.value = Math.max(0, Math.min(points, maxPointsToUse.value))
    
    logger.debug('BasketState', 'Points to use set', {
      requested: points,
      actual: pointsToUse.value,
      max: maxPointsToUse.value,
    })
  }
  
  /**
   * Установить максимальное количество баллов
   */
  function setMaxPoints() {
    pointsToUse.value = maxPointsToUse.value
    
    logger.debug('BasketState', 'Set max points', { maxPoints: maxPointsToUse.value })
  }
  
  /**
   * Включить/выключить использование баллов
   */
  function toggleLoyaltyPoints(enabled: boolean) {
    useLoyaltyPoints.value = enabled
    
    if (!enabled) {
      pointsToUse.value = 0
    }
    
    logger.debug('BasketState', 'Loyalty points toggled', {
      enabled,
      pointsToUse: pointsToUse.value,
    })
  }
  
  /**
   * Обновить информацию о лояльности
   */
  function updateLoyaltyInfo(info: {
    availablePoints: number
    maxPointsToUse: number
    cashbackAmount: number
    cashbackLevel: string
  }) {
    availablePoints.value = info.availablePoints
    maxPointsToUse.value = info.maxPointsToUse
    cashbackAmount.value = info.cashbackAmount
    cashbackLevel.value = info.cashbackLevel
    
    logger.debug('BasketState', 'Loyalty info updated', info)
  }
  
  /**
   * Установить ID зоны доставки
   */
  function setDeliveryZoneId(zoneId: string | null) {
    deliveryZoneId.value = zoneId
    
    logger.debug('BasketState', 'Delivery zone ID set', { zoneId })
  }
  
  /**
   * Установить стоимость доставки
   */
  function setDeliveryCost(cost: number) {
    deliveryCost.value = cost
    
    logger.debug('BasketState', 'Delivery cost set', { cost })
  }
  
  /**
   * Установить минимальную сумму заказа
   */
  function setMinOrderAmount(amount: number) {
    minOrderAmount.value = amount
  }
  
  /**
   * Установить процент сервисного сбора
   */
  function setServiceFeePercent(percent: number) {
    serviceFeePercent.value = percent
  }
  
  /**
   * Установить сумму сервисного сбора
   */
  function setServiceFee(fee: number) {
    serviceFee.value = fee
  }

  /**
   * Установить скидку «подарок за сумму» (цена одной единицы подарка в корзине).
   */
  function setGiftTierDiscount(discount: number) {
    giftTierDiscount.value = Math.max(0, discount || 0)
  }

  /**
   * Установить скидку «подарок за частоту заказов» (цена одной единицы подарка в корзине).
   */
  function setWeeklyGiftDiscount(discount: number) {
    weeklyGiftDiscount.value = Math.max(0, discount || 0)
  }
  
  /**
   * Установить финальную сумму
   */
  function setFinalAmount(amount: number) {
    finalAmount.value = amount
    
    logger.debug('BasketState', 'Final amount set', { amount })
  }
  
  /**
   * Сбросить все состояние корзины
   */
  function resetBasketState() {
    // Промокод
    resetPromocode()
    
    // Баллы
    useLoyaltyPoints.value = false
    pointsToUse.value = 0
    availablePoints.value = 0
    maxPointsToUse.value = 0
    cashbackAmount.value = 0
    cashbackLevel.value = '3%'
    
    // Доставка
    deliveryZoneId.value = null
    deliveryCost.value = 0
    minOrderAmount.value = 0
    
    // Расчеты
    serviceFeePercent.value = 0
    serviceFee.value = 0
    finalAmount.value = 0
    giftTierDiscount.value = 0
    weeklyGiftDiscount.value = 0

    logger.debug('BasketState', 'Full basket state reset')
  }
  
  // ========================================
  // RETURN PUBLIC API
  // ========================================
  
  return {
    // Промокод - state
    promocode,
    promocodeDiscount,
    giftTierDiscount,
    weeklyGiftDiscount,
    isPromocodeApplied,
    isFreeDelivery,
    isPromocodeCompatibleWithLoyalty,
    appliedPromocodeId,
    promocodeError,
    promocodeLoading,
    autoApplyRequested,

    // Баллы - state
    useLoyaltyPoints,
    pointsToUse,
    availablePoints,
    maxPointsToUse,
    cashbackAmount,
    cashbackLevel,
    
    // Доставка - state
    deliveryZoneId,
    deliveryCost,
    minOrderAmount,
    
    // Расчеты - state
    serviceFeePercent,
    serviceFee,
    finalAmount,
    
    // Computed из cart store
    cartItems,
    cartTotal,
    cartItemsCount,
    
    // Вычисления
    amountAfterPromocode,
    amountAfterPoints,
    amountAfterDiscounts,
    totalWithDeliveryAndFee,
    
    // Методы - промокод
    applyPromocode,
    resetPromocode,
    setPromocodeError,
    setPromocodeLoading,
    
    // Методы - баллы
    setPointsToUse,
    setMaxPoints,
    toggleLoyaltyPoints,
    updateLoyaltyInfo,
    
    // Методы - доставка
    setDeliveryZoneId,
    setDeliveryCost,
    setMinOrderAmount,
    
    // Методы - расчеты
    setServiceFeePercent,
    setServiceFee,
    setGiftTierDiscount,
    setWeeklyGiftDiscount,
    setFinalAmount,
    
    // Сброс
    resetBasketState,
  }
})

